import React, { useState, useEffect } from "react";
import { FaTrashAlt, FaEdit } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import pdfIcon from "../assets/images/pdf icon.png";
import depts_unitsData from "../data/eacDepartments.json";
import { FormGroup } from "../components/FormItems";
import supervisorsData from "../data/eacSupervisors.json";
import { LoadingButton } from "../components/loadingButton";
import ConfirmationModal from "../components/ConfirmationModal";
import NotificationModal from "../components/NotificationModal";
import apiClient from "../api/axiosInstance";

const ApplicationDetails = ({ applicationData = {} }) => {
  const [proposedStatus, setProposedStatus] = useState("");
  const [applicationStatus, setApplicationStatus] = useState(
    applicationData.applicationStatus
  );

  const [modalMessage, setModalMessage] = useState(""); // For error/success messages
  const [modalType, setModalType] = useState(""); // "success" or "error"
  const [showModal, setShowModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [internshipDepartment, setInternshipDepartment] = useState(
    applicationData.internshipDepartment || ""
  );
  const [internshipSupervisor, setInternshipSupervisor] = useState(
    applicationData.internshipSupervisor || ""
  );
  const [internshipStartDate, setInternshipStartDate] = useState("");
  const [internshipEndDate, setInternshipEndDate] = useState("");
  const [policyExpirationDate, setPolicyExpirationDate] = useState("");
  const [isEditing, setIsEditing] = useState(false); // State to track editing mode
  const [loading, setLoading] = useState(false);

  // const UPLOADS_SERVER_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  //TODO:Comment during production build
  const UPLOADS_SERVER_URL = "http://localhost:5000";

  const idPassportNumber = applicationData.idPassportNumber;

  const handleSubmit = async () => {
    setLoading(true);
    setIsModalOpen(true);
  };

  const checkDocumentAvailability = async (url, name) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      if (!response.ok) {
        throw new Error(`${name} not found.`);
      }
      return true; // Document is available
    } catch (error) {
      setModalMessage(error.message);
      setModalType("error");
      setShowModal(true);
      return false;
    }
  };

  const updateApplication = async () => {
    try {
      const response = await apiClient.put(
        `/api/update-application/${idPassportNumber}`,
        {
          status: proposedStatus,
          internshipDepartment,
          internshipSupervisor,
          internshipStartDate,
          internshipEndDate,
          applicationStatus,
        }
      );

      setModalMessage("Application updated successfully!"); // Success message
      setModalType("success");
    } catch (error) {
      if (error.response && error.response.data.message) {
        // If the backend sent a message
        setModalMessage(error.response.data.message);
      } else {
        setModalMessage(
          "An error occurred. Please check your network connection and try again."
        );
      }
      setModalType("error");
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };

  useEffect(() => {
    // Function to format date to YYYY-MM-DD
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Set state for internship dates using formatted dates
    if (applicationData.internshipStartDate) {
      setInternshipStartDate(formatDate(applicationData.internshipStartDate));
    }
    if (applicationData.internshipEndDate) {
      setInternshipEndDate(formatDate(applicationData.internshipEndDate));
    }
    if (applicationData.policyExpirationDate) {
      setPolicyExpirationDate(formatDate(applicationData.policyExpirationDate));
    }
  }, [applicationData]);

  const formatDate2 = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(
      new Date(dateString)
    );
  };

  const depts_units = [
    `${applicationData.internshipDepartment}`,
    ...depts_unitsData.units,
  ];
  const supervisors = [
    `${applicationData.internshipSupervisor}`,
    ...supervisorsData.supervisors,
  ];

  return (
    <div>
      <h1 className="md:text-2xl text-xl font-bold text-gray-600 text-center">
        Internship Application Details
      </h1>
      <div className="flex flex-col md:flex-row gap-4 pt-5 p-2 lg:p-5 lg:text-base text-sm">
        {/* Left Column: Applicant Details */}
        <div className="w-full md:w-1/4 mx-auto pt-12 p-2 border rounded-lg shadow-md flex flex-col items-center bg-yellow-100 bg-opacity-60">
          <div className="border-4 border-gray-800 rounded-full mb-4 p-3 flex items-center justify-center">
            <CiUser size={120} />
          </div>
          <h1 className="text-xl font-bold mb-2 text-center text-green-700">
            {`${applicationData.surname} ${applicationData.firstName} ${applicationData.otherNames}`}
          </h1>
          <div className="flex flex-col items-center text-center mb-4 space-y-3 font-semibold">
            <p>ID/Passport No: {applicationData.idPassportNumber}</p>
            <p>
              E-mail:{" "}
              <a
                href={`mailto:${applicationData.email}`}
                className="text-blue-600 hover:underline"
              >
                {applicationData.email || "email@example.com"}
              </a>
            </p>
            <p>Phone: {applicationData.phoneNumber}</p>
            <p>Nationality: {applicationData.nationality}</p>
            <p>Address: {applicationData.address || "Unavailable"}</p>
            <p className="pt-6">
              Date of Application: {formatDate2(applicationData.createdAt)}
            </p>
          </div>

          <h2 className="font-bold text-lg text-green-700 mt-4 mb-2 underline text-center">
            Uploaded Documents
          </h2>
          <div className="flex flex-col space-y-2">
            {[
              {
                name: "ID/Passport Document",
                url: `${UPLOADS_SERVER_URL}/${applicationData.identificationDocument}`,
              },
              {
                name: "Academic Document",
                url: `${UPLOADS_SERVER_URL}/${applicationData.academicDocuments}`,
              },
              {
                name: "Insurance Cover Document",
                url: `${UPLOADS_SERVER_URL}/${applicationData.insuranceDocument}`,
              },
            ].map((doc, index) => (
              <React.Fragment key={index}>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline flex items-center font-semibold"
                  onClick={(e) => {
                    // Check availability before navigating
                    e.preventDefault();
                    checkDocumentAvailability(doc.url, doc.name).then(
                      (isAvailable) => {
                        if (isAvailable) {
                          window.open(doc.url, "_blank");
                        }
                      }
                    );
                  }}
                >
                  <img
                    src={pdfIcon}
                    alt="PDF Icon"
                    className="w-[50px] h-[50px] mr-2"
                  />
                  {doc.name}
                </a>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Right Column: Update Application Details */}
        <div className="w-full md:w-3/4 p-3 lg:p-4 border rounded-lg shadow-md ">
          <div className="flex flex-col space-y-4">
            <div className="md:flex md:flex-row md:justify-between">
              {/* Action Buttons */}
              <div className="flex flex-row space-x-4  lg:space-x-10 items-center">
                <button
                  onClick={() => setIsEditing(true)} // Enable editing
                  className="bg-blue-500 hover:bg-blue-700 rounded-[10px] p-3 px-4 lg:px-6 text-xs lg:text-sm font-medium text-white flex items-center space-x-2"
                >
                  <FaEdit />
                  <span>Edit Application</span>
                </button>
                <button className="bg-red-500 hover:bg-red-800 rounded-[10px] p-3 px-4 lg:px-6 text-xs lg:text-sm font-medium text-white flex items-center space-x-2">
                  <FaTrashAlt />
                  <span>Delete Application</span>
                </button>
              </div>

              {/* Combo Box for Application Status */}
              <div className="flex flex-col md:space-x-4 md:flex-row md:items-center md:justify-end mt-2">
                <label
                  htmlFor="application-status-field"
                  className="text-gray-700 lg:text-base text-sm font-semibold"
                >
                  Application Status
                </label>
                <select
                  className={`lg:text-base text-sm border-2 border-green-700 hover:bg-gray-200 rounded-[10px] mt-2 md:mt-0 px-4 py-[6px] bg-white text-green-700 font-semibold ${
                    !isEditing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  name="Application Status"
                  id="application-status-field"
                  onChange={(e) => setApplicationStatus(e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="">{applicationData.applicationStatus}</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Archived">Archived</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Education Section */}
            <h1 className="font-semibold text-white lg:text-lg bg-green-700 bg-opacity-80 py-2 pl-4 rounded-md">
              Education Details
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  label: "Institution",
                  value: applicationData.institutionName,
                },
                { label: "Course", value: applicationData.courseProgram },
                { label: "Current Year", value: applicationData.currentYear },
                {
                  label: "Academic Award",
                  value: applicationData.academicQualification,
                },
              ].map((item, index) => (
                <div key={index} className="flex items-center">
                  <label className="mr-2">{item.label}:</label>
                  <input
                    type="text"
                    value={item.value || ""}
                    disabled
                    className="border p-2 rounded w-full font-semibold"
                  />
                </div>
              ))}
            </div>

            {/* Internship Section */}
            <h1 className="font-semibold text-white lg:text-lg bg-green-700 bg-opacity-80 py-2 pl-4 rounded-md">
              Internship
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <label className="mr-2">Department:</label>
                <select
                  className={`border p-2 rounded w-full font-semibold${
                    !isEditing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  name="Department"
                  id="department-field"
                  onChange={(e) => setInternshipDepartment(e.target.value)}
                  disabled={!isEditing}
                >
                  {depts_units.map((depts_units, index) => (
                    <option key={index} value={internshipDepartment}>
                      {depts_units}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <label className="mr-2">Supervisor:</label>
                <select
                  className={`border p-2 rounded w-full font-semibold${
                    !isEditing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  name="Supervisor"
                  id="supervisor-field"
                  onChange={(e) => setInternshipSupervisor(e.target.value)}
                  disabled={!isEditing}
                >
                  {supervisors.map((supervisor, index) => (
                    <option key={index} value={supervisor}>
                      {supervisor}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <label className="mr-2">Internship Start Date:</label>
                <input
                  type="date"
                  value={internshipStartDate}
                  onChange={(e) => setInternshipStartDate(e.target.value)}
                  disabled={!isEditing}
                  className={`border p-2 rounded w-full font-semibold ${
                    !isEditing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                />
              </div>
              <div className="flex items-center">
                <label className="mr-2">Internship End Date:</label>
                <input
                  type="date"
                  value={internshipEndDate}
                  onChange={(e) => setInternshipEndDate(e.target.value)}
                  disabled={!isEditing}
                  className={`border p-2 rounded w-full font-semibold ${
                    !isEditing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                />
              </div>
            </div>

            {/* Insurance & Emergency Section */}
            <h1 className="font-semibold text-white lg:text-lg bg-green-700 bg-opacity-80 py-2 pl-4 rounded-md">
              Insurance & Emergency
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center">
                <label className="mr-2">Insurance Company:</label>
                <input
                  type="text"
                  value={applicationData.insuranceCompany || ""}
                  disabled
                  className="border p-2 rounded w-full font-semibold"
                />
              </div>
              <div className="flex items-center">
                <label className="mr-2">Policy No.:</label>
                <input
                  type="text"
                  value={applicationData.insurancePolicyNumber || ""}
                  disabled
                  className="border p-2 rounded w-full font-semibold"
                />
              </div>
              <div className="flex items-center">
                <label className="mr-2">Expiry Date:</label>
                <input
                  type="date"
                  value={policyExpirationDate}
                  onChange={(e) => setPolicyExpirationDate(e.target.value)}
                  disabled
                  className="border p-2 rounded w-full font-semibold"
                />
              </div>
            </div>

            {/* Emergency Contact Section */}
            <h2 className="font-semibold mt-4 text-green-700">
              Emergency Contact
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-5">
              <div className="flex items-center">
                <input
                  type="text"
                  value={applicationData.emergencyContactPerson || ""}
                  disabled
                  className="border p-2 rounded w-full font-semibold"
                />
              </div>
              <div className="flex items-center">
                <label className="mr-2">Phone Number:</label>
                <input
                  type="text"
                  value={applicationData.emergencyContactPhone || ""}
                  disabled
                  className="border p-2 rounded w-full font-semibold"
                />
              </div>
              <div className="flex items-center">
                <label className="mr-2">Email Address:</label>
                <input
                  type="text"
                  value={applicationData.emergencyContactEmail || ""}
                  disabled
                  className="border p-2 rounded w-full font-semibold"
                />
              </div>
            </div>

            {/* Update and Cancel Buttons */}
            <div className="flex flex-row justify-center space-x-4 md:space-x-4 mt-4">
              {/* Update Button with Loading Spinner */}
              <LoadingButton
                disabled={!isEditing}
                isLoading={loading}
                onClick={handleSubmit}
                buttonText="Update Application"
                loadingText="Updating Application..."
                className="bg-green-700 hover:bg-green-800 rounded-[10px] p-3 font-semibold text-[13px] lg:text-[15px] lg:px-8 text-white"
              />
              <button
                disabled={!isEditing}
                onClick={() => {
                  setLoading(false);
                  setIsEditing(false);
                }}
                className={`bg-gray-500 hover:bg-gray-400 rounded-[10px] p-3 font-semibold text-[13px] lg:text-[15px] lg:px-14 px-9 text-white ${
                  !isEditing ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal to confirm updates to internship application */}
      <ConfirmationModal
        isOpen={isModalOpen}
        title="Confirm Internship Application Update"
        message="Are you sure you want to update the details of this application?"
        onClose={() => {
          setIsModalOpen(false);
          setLoading(false);
        }}
        onConfirm={() => {
          updateApplication();
          setIsModalOpen(false);
        }}
      />

      {/* Modal to show success/error message on update */}
      <NotificationModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        message={modalMessage}
        modalType={modalType}
      />
    </div>
  );
};

export default ApplicationDetails;
